import {
  Box,
  Checkbox,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import {
  Check,
  Clear,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from "@mui/icons-material";
import {
  getFormattedDate,
  isContainSameClaimOptionValues,
  getOptionsArray,
} from "data/functions";
import { ServiceSummary, User, UserWithClaims } from "data/interfaces";
import { red } from "@mui/material/colors";
import { useEffect, useState } from "react";

interface RoleRowProps {
  showDropdown: boolean;
  userWithClaims: UserWithClaims;
  service: ServiceSummary;
  isSelected: (user: User) => boolean;
  onSelect: (checked: boolean, user: User) => void;
  userHasClaimType: (claimType: string, user: User) => boolean | undefined;
  userHasClaimTypeAndValue: (
    claimType: string,
    claimValue: string,
    user: User
  ) => boolean | undefined;
}

const RoleRow = ({
  showDropdown,
  userWithClaims,
  service,
  isSelected,
  onSelect,
  userHasClaimType,
  userHasClaimTypeAndValue,
}: RoleRowProps) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if(showDropdown) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [service, showDropdown]);

  const getExpriryDate = (userWithClaims: UserWithClaims) => {
    const expiryDate = service.activeSeats.find(
      (s) => s.email === userWithClaims.user.email
    )?.endDate;

    return expiryDate !== undefined ? getFormattedDate(expiryDate) : null;
  };

  const isDisplayOneTableInDropdown = () => {
    if (showDropdown) {
      return isContainSameClaimOptionValues(service);
    } else {
      return false;
    }
  };

  return (
    <>
      <TableRow hover sx={{ cursor: "pointer" }}>
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected(userWithClaims.user)}
            onChange={(e, checked) => onSelect(checked, userWithClaims.user)}
          />
        </TableCell>
        <TableCell>{userWithClaims.user.name || "New user"}</TableCell>
        <TableCell>{userWithClaims.user.displayEmail}</TableCell>
        <TableCell>{getExpriryDate(userWithClaims)}</TableCell>
        {!showDropdown &&
          service.serviceClaims.map((claim, index) =>
            claim.options.length >= 1 ? (
              claim.options.map((option, optionIndex) => (
                <TableCell key={optionIndex} align="center">
                  {userHasClaimTypeAndValue(
                    claim.claimType,
                    option.claimValue,
                    userWithClaims.user
                  ) ? (
                    <Check color="success" />
                  ) : (
                    <Clear sx={{ color: red[500] }} />
                  )}
                </TableCell>
              ))
            ) : (
              <TableCell key={index} align="center">
                {userHasClaimType(claim.claimType, userWithClaims.user) ? (
                  <Check color="success" />
                ) : (
                  <Clear sx={{ color: red[500] }} />
                )}
              </TableCell>
            )
          )}
        {showDropdown && (
          <TableCell>
            <IconButton onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            {isDisplayOneTableInDropdown() && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell></TableCell>
                    {getOptionsArray(service).map((option, optionIndex) => (
                      <TableCell
                        key={optionIndex}
                        align="center"
                        sx={{ fontWeight: "normal" }}
                      >
                        {option.claimValueDescription}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {service.serviceClaims.map((claim, index) => (
                    <TableRow key={index}>
                      <Tooltip placement="left" title={claim.friendlyName}>
                        <TableCell sx={{ fontWeight: "medium" }}>
                          {claim.shortName}
                        </TableCell>
                      </Tooltip>
                      {getOptionsArray(service).map((option, optionIndex) => (
                        <TableCell key={optionIndex} align="center">
                          {userHasClaimTypeAndValue(
                            claim.claimType,
                            option.claimValue,
                            userWithClaims.user
                          ) ? (
                            <Check color="success" />
                          ) : (
                            <Clear sx={{ color: red[500] }} />
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {!isDisplayOneTableInDropdown() &&
              service.serviceClaims.map((claim, index) => {
                const sortedOptions = claim.options.sort((item1, item2) =>
                  item1.claimValueDescription > item2.claimValueDescription
                    ? 1
                    : -1
                );
                return (
                  <Box key={index}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell></TableCell>
                          {sortedOptions.map((option, optionIndex) => (
                            <TableCell
                              key={optionIndex}
                              align="center"
                              sx={{ fontWeight: "normal" }}
                            >
                              {option.claimValueDescription}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <Tooltip placement="left" title={claim.friendlyName}>
                            <TableCell
                              sx={{ fontWeight: "medium", width: "20%" }}
                            >
                              {claim.shortName}
                            </TableCell>
                          </Tooltip>
                          {sortedOptions.map((option, optionIndex) => (
                            <TableCell key={optionIndex} align="center">
                              {userHasClaimTypeAndValue(
                                claim.claimType,
                                option.claimValue,
                                userWithClaims.user
                              ) ? (
                                <Check color="success" />
                              ) : (
                                <Clear sx={{ color: red[500] }} />
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Box>
                );
              })}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default RoleRow;
